<template>
  <gc-dashboard-empty-state
    v-if="!productHistory.length"
    title="No Product History"
    message="There are no product history records for this customer."
    :height="gridHeight"
  />
  <gc-filter-split-view
    v-else
    class="ph-filter-split-view"
    :showLeftContainer="showLeftContainer"
    :showRightContainer="showRightContainer"
    :rightContainerWidth="280"
  >
    <template #leftContent>
      <DxDataGrid
        ref="ph-grid"
        :data-source="productHistory"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="true"
        :column-auto-width="true"
        :columns="columns"
        :height="gridHeight"
        :column-chooser="{ enabled: true, allowSearch: true, mode: 'select', height: '100%', width: 300 }"
        :search-panel="{ visible: true, highlightCaseSensitive: true, highlightSearchText: false }"
        key-expr="ProductCode"
        @cell-click="onCellClick"
        @cell-prepared="onCellPrepared"
        @toolbar-preparing="onToolbarPreparing"
      >
        <DxPaging :enabled="false" />
        <DxPager :visible="false" />
        <DxScrolling mode="virtual" row-rendering-mode="virtual" :preload-enabled="true" />

        <template #item-code-cell-template="{ data }">
          <router-link
            class="ph-code-link"
            target="_blank"
            :to="`/product/review/${data.data.ProductCode}`"
          >
            {{ data.data.ProductCode }}
          </router-link>
        </template>

        <template #invoice-cell-template="{ data }">
          <router-link
            class="ph-transaction-link"
            target="_blank"
            :to="`/invoice/review/${data.data.InvoiceNo}`"
          >
            {{ data.data.InvoiceNo }}
          </router-link>
        </template>

        <template #sales-order-cell-template="{ data }">
          <router-link
            v-if="data.data.SalesOrder > 0"
            class="ph-transaction-link"
            target="_blank"
            :to="`/sales-order/review/${data.data.SalesOrder}`"
          >
            {{ data.data.SalesOrder }}
          </router-link>
          <span v-else>{{ data.data.SalesOrder }}</span>
        </template>

        <template #price-group-cell-template="{ data }">
          <div class="ph-price-group-cell">
            <span class="ph-product-desc">{{ data.data.PriceGroupShortDescription }}</span>
            <span class="ph-code-secondary">{{ data.data.PriceGroupId }}</span>
          </div>
        </template>
      </DxDataGrid>
    </template>

    <template #rightContent>
      <div class="ph-filter-panel-wrapper" :style="{ maxHeight: gridHeight + 'px' }">
        <gc-filter-panel
          :toolbarVisible="true"
          :toolbar="filterToolbar"
          :formItemData="filterFormItems"
          :setGridPanelFilter="setGridPanelFilter"
          :gridFoolter="false"
          :from="'model'"
          :isNormal="false"
          :persistenceKey="undefined"
          :overWriteClear="true"
          :heightAdjustment="filterHeightAdjustment"
          ref="gc-filter-panel"
        />
      </div>
    </template>
  </gc-filter-split-view>
</template>

<script>
import { DxDataGrid, DxPaging, DxPager, DxScrolling } from "devextreme-vue/data-grid";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import { getDecimalFormat } from "../../_store/helpers";
import { CompanyService } from "../../_services/api/accounts/company.service";
import GcDashboardEmptyState from "./gc-dashboard-empty-state.vue";
import GcFilterSplitView from "../gc-filter-split-view.vue";
import GcFilterPanel from "../gc-filter-panel.vue";
import DataGridMixin from "../../_mixins/data-grid.mixin";

const ALWAYS_LINKED_FIELDS = ["ProductCode", "InvoiceNo"];

const NEGATIVE_HIGHLIGHT_FIELDS = [
  "SalesUMQty",
  "SalesUMStandardCost",
  "UnitPriceFX",
  "UnitPriceBase",
  "ExTotalBase",
  "GrossProfitPcnt",
  "GrossProfitBase",
];

export default {
  name: "GcDashboardProductHistoryCard",
  mixins: [DataGridMixin],
  components: {
    DxDataGrid,
    DxPaging,
    DxPager,
    DxScrolling,
    GcDashboardEmptyState,
    GcFilterSplitView,
    GcFilterPanel,
  },
  props: {
    productHistory: {
      type: Array,
      required: true,
    },
    gridHeight: {
      type: [Number, String],
      default: 360,
    },
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    expandable: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      baseCurrencyCode: "",
      showLeftContainer: true,
      showRightContainer: false,
      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
          },
        },
      },
    };
  },
  async created() {
    try {
      const response = await CompanyService.getCompany({ select: ["CurrencyCode"] });
      this.baseCurrencyCode = (response.data.value && response.data.value[0] && response.data.value[0].CurrencyCode) || "";
    } catch (e) {
      this.baseCurrencyCode = "";
    }
  },
  computed: {
    decimalFormat() {
      return getDecimalFormat("Decimal-16-2").ReadOnlyFormat;
    },
    percentFormat() {
      return getDecimalFormat("Decimal-16-2P").ReadOnlyFormat;
    },
    baseCurrencyLabel() {
      return this.baseCurrencyCode || "Base Currency";
    },
    filterHeightAdjustment() {
      return Number(this.gridHeight) - 15;
    },
    filterFormItems() {
      return [
        {
          screenField: "Product",
          filterIndex: 1,
        },
        {
          screenField: "Invoice",
          filterDataField: "InvoiceNo",
          filterLabel: "Invoice #",
          filterIndex: 2,
          type: "Integer",
        },
        {
          screenField: "Invoice",
          filterDataField: "SalesOrder",
          filterLabel: "Sales Order #",
          filterIndex: 3,
          type: "Integer",
        },
        {
          screenField: "ProductCategory1",
          filterLabel: "Product Category 1",
          filterDataField: "ProductCategory1",
          filterIndex: 4,
        },
        {
          screenField: "ProductCategory2",
          filterLabel: "Product Category 2",
          filterDataField: "ProductCategory2",
          filterIndex: 5,
        },
        {
          screenField: "ProductCategory3",
          filterLabel: "Product Category 3",
          filterDataField: "ProductCategory3",
          filterIndex: 6,
        },
        {
          screenField: "ProductCategory4",
          filterLabel: "Product Category 4",
          filterDataField: "ProductCategory4",
          filterIndex: 7,
        },
      ].concat(this.getAttributeColumns(7, false, false));
    },
    columns() {
      return [
        {
          caption: "Product",
          dataField: "ProductCode",
          cellTemplate: "item-code-cell-template",
          width: ColumnWidth.ProductCode.width,
          minWidth: ColumnWidth.ProductCode.minWidth,
        },
        {
          caption: "Description",
          dataField: "ProductDescription1",
          minWidth: ColumnWidth.ProductDescription.minWidth,
        },
        {
          caption: "Date",
          dataField: "MovementDate",
          dataType: "date",
          format: "dd MMM yyyy",
          sortOrder: "desc",
          width: ColumnWidth.Date.width,
          minWidth: ColumnWidth.Date.minWidth,
          allowSearch: false,
        },
        {
          caption: "Invoice #",
          dataField: "InvoiceNo",
          cellTemplate: "invoice-cell-template",
          alignment: "left",
          width: ColumnWidth.InvoiceNumber.width,
          minWidth: ColumnWidth.InvoiceNumber.minWidth,
          allowSearch: false,
        },
        {
          caption: "Sales Order #",
          dataField: "SalesOrder",
          cellTemplate: "sales-order-cell-template",
          alignment: "left",
          width: ColumnWidth.SalesOrderNumber.width,
          minWidth: ColumnWidth.SalesOrderNumber.minWidth,
          allowSearch: false,
        },
        {
          caption: "Sales UM",
          dataField: "SalesUM",
          width: ColumnWidth.UnitOfMeasure.width,
          minWidth: ColumnWidth.UnitOfMeasure.minWidth,
          allowSearch: false,
        },
        {
          caption: "Sales UM Qty",
          dataField: "SalesUMQty",
          format: this.decimalFormat,
          alignment: "right",
          width: ColumnWidth.Quantity.width,
          minWidth: ColumnWidth.Quantity.minWidth,
          allowSearch: false,
        },
        {
          caption: "Currency",
          dataField: "FXCurrency",
          width: ColumnWidth.CurrencyCode.width,
          minWidth: ColumnWidth.CurrencyCode.minWidth,
          allowSearch: false,
        },
        {
          caption: `Sales UM Std Cost (${this.baseCurrencyLabel})`,
          dataField: "SalesUMStandardCost",
          format: this.decimalFormat,
          alignment: "right",
          width: ColumnWidth.Price2DecimalPoints.width,
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          allowSearch: false,
        },
        {
          caption: "Sales UM Price (FX)",
          dataField: "UnitPriceFX",
          format: this.decimalFormat,
          alignment: "right",
          width: ColumnWidth.Price2DecimalPoints.width,
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          allowSearch: false,
        },
        {
          caption: `Sales UM Price (${this.baseCurrencyLabel})`,
          dataField: "UnitPriceBase",
          format: this.decimalFormat,
          alignment: "right",
          width: ColumnWidth.Price2DecimalPoints.width,
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          allowSearch: false,
        },
        {
          caption: `Ex Total (${this.baseCurrencyLabel})`,
          dataField: "ExTotalBase",
          format: this.decimalFormat,
          alignment: "right",
          width: ColumnWidth.Price2DecimalPoints.width,
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          allowSearch: false,
        },
        {
          caption: "GP %",
          dataField: "GrossProfitPcnt",
          format: this.percentFormat,
          alignment: "right",
          width: ColumnWidth.Price2DecimalPoints.width,
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          allowSearch: false,
        },
        {
          caption: `GP (${this.baseCurrencyLabel})`,
          dataField: "GrossProfitBase",
          format: this.decimalFormat,
          alignment: "right",
          width: ColumnWidth.Price2DecimalPoints.width,
          minWidth: ColumnWidth.Price2DecimalPoints.minWidth,
          allowSearch: false,
        },
        {
          caption: "Price Group",
          dataField: "PriceGroupId",
          cellTemplate: "price-group-cell-template",
          width: ColumnWidth.ShortDescription.width,
          minWidth: ColumnWidth.ShortDescription.minWidth,
          allowSearch: false,
        },
      ];
    },
  },
  methods: {
    onToolbarPreparing(e) {
      if (this.title) {
        e.toolbarOptions.items.unshift({
          location: "before",
          text: this.title,
          cssClass: "ph-toolbar-title",
        });
      }

      if (this.subtitle) {
        e.toolbarOptions.items.push({
          location: "center",
          text: this.subtitle,
          cssClass: "ph-toolbar-subtitle",
        });
      }

      e.toolbarOptions.items.push({
        location: "after",
        locateinmenu: "auto",
        widget: "dxButton",
        options: {
          icon: "filter",
          elementAttr: { id: "ph-filter-btn" },
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
            var item = document.getElementById("ph-filter-btn");
            if (item) item.classList.add("dx-state-invisible");
          },
          visible: !this.showRightContainer,
        },
      });

      if (this.expandable) {
        e.toolbarOptions.items.push({
          location: "after",
          widget: "dxButton",
          options: {
            icon: this.expanded ? "collapse" : "expand",
            hint: this.expanded ? "Collapse" : "Full screen",
            stylingMode: "text",
            onClick: () => this.$emit("toggle-expand"),
          },
        });
      }
    },
    // eslint-disable-next-line no-unused-vars
    setGridPanelFilter(e, valueChanged) {
      const dataGrid = this.$refs["ph-grid"] && this.$refs["ph-grid"].instance;
      if (!dataGrid) return;

      if (e && e.length > 0) {
        dataGrid.filter(e);
      } else {
        dataGrid.clearFilter();
      }
    },
    onCellClick(e) {
      if (e.rowType !== "data") {
        return;
      }

      const field = e.column.dataField;

      if (ALWAYS_LINKED_FIELDS.includes(field)) {
        return;
      }

      if (field === "SalesOrder" && e.data.SalesOrder > 0) {
        return;
      }

      this.$emit("row-click", e.data);
    },
    onCellPrepared(e) {
      if (
        e.rowType === "data" &&
        NEGATIVE_HIGHLIGHT_FIELDS.includes(e.column.dataField) &&
        e.data[e.column.dataField] < 0
      ) {
        e.cellElement.classList.add("negative-decimal");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ph-price-group-cell {
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}

.ph-product-desc {
  font-size: 12px;
}

.ph-code-secondary {
  font-size: 10px;
  color: #667085;
}

.ph-code-link {
  color: #35baf6;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.ph-transaction-link {
  color: #35baf6;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.ph-filter-panel-wrapper {
  overflow-y: auto;
}

.ph-filter-split-view {
  ::v-deep .right-container {
    align-self: flex-start;
  }

  ::v-deep .gc-filter-form {
    height: auto !important;
  }

  ::v-deep .ph-toolbar-title {
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }

  ::v-deep .ph-toolbar-subtitle {
    font-size: 10px;
    color: #999;
    white-space: nowrap;
  }
}
</style>
